import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import Logo from "../Logo";
import { LayoutDashboard, Kanban, MessagesSquare, Home, Users, Wrench, BadgePercent, ScrollText, LogOut, Sparkles, Mail, Rocket, Contact, DoorOpen, Receipt, ClipboardCheck, FolderArchive, UserCheck } from "lucide-react";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/pipeline", label: "Deal Pipeline", icon: Kanban },
  { to: "/goals", label: "Goal Tracker", icon: Rocket },
  { to: "/expenses", label: "Expenses", icon: Receipt },
  { to: "/compliance", label: "Compliance", icon: ClipboardCheck },
  { to: "/archives", label: "Broker Archives", icon: FolderArchive },
  { section: "Agent Playbook" },
  { to: "/scripts", label: "Scripts & Dialogues", icon: MessagesSquare },
  { to: "/listing", label: "Listing System", icon: Home },
  { to: "/buyer", label: "Buyer System", icon: Users },
  { section: "Brokerage" },
  { to: "/tools", label: "Tools & Tech", icon: Wrench },
  { to: "/newsletter", label: "Newsletter Builder", icon: Mail },
  { to: "/contacts", label: "Contacts List", icon: Contact },
  { to: "/boost", label: "Property Boost", icon: Rocket },
  { to: "/open-houses", label: "Open House Sign-In", icon: DoorOpen },
  { to: "/tiers", label: "Tiers & Splits", icon: BadgePercent },
  { to: "/glossary", label: "GA Law Glossary", icon: ScrollText },
];

const ADMIN_LINK = { to: "/admin", label: "Back Office", icon: Sparkles };
const CLIENT_LEADS_LINK = { to: "/client-leads", label: "Client Leads", icon: UserCheck };

export default function Sidebar() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const items = user?.role === "admin"
    ? [...NAV, { section: "Admin" }, CLIENT_LEADS_LINK, ADMIN_LINK]
    : NAV;

  return (
    <aside className="w-64 shrink-0 h-screen sticky top-0 bg-[#ffffff] border-r border-[#d1cece] flex flex-col" data-testid="sidebar">
      <div className="px-6 py-7 border-b border-[#d1cece]">
        <Logo variant="black" height={42} />
        <div className="overline mt-3">Realty & Associates</div>
        <div className="mt-2 text-[10px] font-semibold tracking-[0.18em] uppercase text-[#bf858e]" data-testid="brand-motto">
          Real Estate Integrity at its CORE
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto py-5 px-3">
        {items.map((it, i) => {
          if (it.section) return (
            <div key={`s-${i}`} className="overline px-3 pt-5 pb-2" data-testid={`sidebar-section-${it.section}`}>{it.section}</div>
          );
          const Icon = it.icon;
          return (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.end}
              data-testid={`nav-${it.to.replace(/\//g, "") || "dashboard"}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg mb-1 text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-[#e1b7c1]/50 text-[#050303]"
                    : "text-[#050303]/70 hover:bg-[#e1b7c1]/30 hover:text-[#050303]"
                }`
              }
            >
              <Icon size={17} strokeWidth={1.5} />
              {it.label}
            </NavLink>
          );
        })}
      </nav>

      <div className="border-t border-[#d1cece] p-4">
        <div className="text-sm font-semibold text-[#050303]" data-testid="sidebar-user-name">{user?.name}</div>
        <div className="text-xs text-[#050303]/55 capitalize">{user?.role}</div>
        <button
          onClick={() => { logout(); nav("/login"); }}
          className="mt-3 w-full flex items-center gap-2 text-sm text-[#050303]/70 hover:text-[#bf858e] transition-colors"
          data-testid="logout-btn"
        >
          <LogOut size={15} strokeWidth={1.5} /> Sign out
        </button>
      </div>
    </aside>
  );
}
